/**
 * Small pieces shared by the WhatsApp board, table and side panel: the stage
 * badge and the one marker a card shows.
 */

import { StatusBadge } from '../../../../components/DataTable';
import type { StatusStyle } from '../../../../config/statusConfig';
import { getStatusStyle } from '../../../../config/statusConfig';
import type { LiveChat, Stage } from './liveData';

// Labels are WhatsApp's own; the colours are the canonical palette's.
const STAGE_CONFIG: Record<Stage, StatusStyle> = {
  unassigned: { ...getStatusStyle('overdue'), label: 'Unassigned' },
  waiting_operator: { ...getStatusStyle('pending'), label: 'Waiting on operator' },
  waiting_customer: { ...getStatusStyle('sent'), label: 'Waiting on customer' },
  booked: { ...getStatusStyle('paid'), label: 'Booked' },
  closed: { ...getStatusStyle('cancelled'), label: 'Closed' },
};

export function StageBadge({ stage }: { stage: Stage }) {
  return <StatusBadge status={stage} config={STAGE_CONFIG} />;
}

/** The one thing a board card needs you to see first. */
export function CardMarker({ chat }: { chat: LiveChat }) {
  const badge = (key: string, label: string) => <StatusBadge status={key} config={{ [key]: { ...getStatusStyle(key), label } }} />;
  if (chat.escalated) return badge('overdue', 'Escalated');
  if (chat.dueMinutes !== null && chat.dueMinutes <= 0) return badge('overdue', 'Reply overdue');
  if (chat.dueMinutes !== null) return badge('pending', `⏱ ${chat.dueMinutes} min left`);
  // Unpaid and Stale say why they sit there; booked chats keep their tag (Gary, 16 Oct).
  if (chat.group === 'unpaid') return badge('sent', 'Invoice unpaid');
  if (chat.group === 'stale') return badge('draft', chat.booked ? 'Booked · template only' : 'Template only');
  if (chat.booked) return badge('paid', 'Booked');
  if (chat.inbox === 'billing') return badge('sent', 'Accounts');
  if (chat.inbox === 'customer_service') return badge('sent', 'Customer service');
  return <span className="text-xs text-muted">{chat.source}</span>;
}
